import {
  hasPackageData,
  hasHighlightData,
  type EventFormDataType,
  type EventSponsorShipPayloadType,
} from '@/schemas/eventForm.schema'
import type {
  createEventPayload,
  EventGuestUser,
  EventHighlight,
} from '@/api/eventApi'
import type {
  EventTicket,
  EventImage,
  EventUser,
} from '@/pages/Dashboard/AddEditEvent'
import { EVENT_FORM_STATUS } from '@/constants/event'
import type { Path, UseFormReturn } from 'react-hook-form'
import type { EventFormDataType as EventAdminEventFormDataType } from '@/schemas/eventAdminEventForm.schema'
import { combineDateTime } from './dateUtils'

export const buildDraftPayload = async <
  T extends EventFormDataType | EventAdminEventFormDataType,
>(
  formData: T,
  existingEventId?: string,
  eventPdf?: string,
  eventInfoPdf?: string
): Promise<Partial<createEventPayload>> => {
  const images: Pick<EventImage, 'id' | 'key'>[] = (formData.eventImages ?? [])
    .map(img => {
      if (!img) return undefined
      const entry: Pick<EventImage, 'id' | 'key'> = { key: img.key }
      if (existingEventId && img.id && !img.isNew) {
        entry.id = img.id
      }
      return entry.key ? entry : undefined
    })
    .filter((img): img is Pick<EventImage, 'id' | 'key'> => img !== undefined)

  const guestUsers: EventGuestUser[] =
    Array.isArray(formData.users) && formData.users.length
      ? formData.users
          .filter(
            user =>
              user.fullName ||
              user.avatarFile ||
              user.designation ||
              user.profile
          )
          .map(user => {
            const newUserData: EventGuestUser = {
              name: user.fullName,
              designation: user.designation,
              profile: user.profile ?? '',
            }
            if (existingEventId && !user.isNew) {
              newUserData.id = user.id
            }
            return newUserData
          })
      : []

  let eventTicketData: EventTicket[] = []
  if (
    formData.registrationType === 'paid' &&
    Array.isArray(formData.eventTicket)
  ) {
    eventTicketData = formData.eventTicket.map(ticket => ({
      name: ticket.name,
      description: ticket.description,
      perks: ticket.perks,
      price: Number(Number(ticket.price ?? 0).toFixed(6)),
      ...(existingEventId && !ticket.isNew ? { id: ticket.id } : {}),
      isSubEventTicket: ticket.isSubEventTicket ?? false,
      maxAttendeesLimit: Number(ticket.maxAttendeesLimit),
    }))
  }

  const packagePlans =
    'sponsorshipPackages' in formData &&
    Array.isArray(formData.sponsorshipPackages)
      ? formData.sponsorshipPackages
      : []

  const filledPackages = packagePlans
    .filter(pkg => {
      const hasData = hasPackageData(pkg)
      return hasData
    })
    .map(pkg => {
      const packageData: Partial<EventSponsorShipPayloadType> & {
        id?: string
      } = {}

      if (pkg.name) packageData.name = pkg.name
      if (pkg.description) packageData.description = pkg.description
      if (pkg.price) packageData.price = Number(pkg.price)
      if (pkg.availablePlacements && pkg.availablePlacements.length > 0) {
        packageData.availablePlacements = pkg.availablePlacements
      }
      if (pkg.priority) packageData.priority = Number(pkg.priority)
      if (pkg.heroDuration) packageData.heroDuration = Number(pkg.heroDuration)
      if (pkg.maxSponsorshipsLimit) {
        packageData.maxSponsorshipsLimit = Number(pkg.maxSponsorshipsLimit)
      }
      if (existingEventId && pkg.id && !pkg.isNew) {
        packageData.id = pkg.id
      }

      return packageData as EventSponsorShipPayloadType
    })

  const sponsorshipPackages =
    filledPackages.length > 0 ? filledPackages : undefined

  const highlightPlans =
    'eventHighlights' in formData && Array.isArray(formData.eventHighlights)
      ? formData.eventHighlights
      : []

  const filledHighlights = highlightPlans
    .filter(hasHighlightData)
    .map(highlight => {
      const startDateTime = highlight.startDate
        ? combineDateTime(
            highlight.startDate,
            highlight.startTime
          )?.toISOString()
        : undefined
      const endDateTime = highlight.endDate
        ? combineDateTime(highlight.endDate, highlight.endTime)?.toISOString()
        : undefined

      return {
        ...(highlight.title && { title: highlight.title }),
        ...(highlight.shortDescription && {
          description: highlight.shortDescription,
        }),
        ...(highlight.eventImage && { image: highlight.eventImage }),
        ...(startDateTime && { startDateTime }),
        ...(endDateTime && { endDateTime }),
        ...(existingEventId &&
          highlight.id &&
          !highlight.isNew && { id: highlight.id }),
      } as EventHighlight
    })

  const activityHighlights =
    filledHighlights.length > 0 ? filledHighlights : undefined

  const startDate = 'startDate' in formData ? formData.startDate : undefined
  const startTime = 'startTime' in formData ? formData.startTime : undefined
  const endDate = 'endDate' in formData ? formData.endDate : undefined
  const endTime = 'endTime' in formData ? formData.endTime : undefined

  const startDateTime = combineDateTime(startDate, startTime)
  const endDateTime = combineDateTime(endDate, endTime)

  return {
    name: formData.eventName || undefined,
    categoryId: formData.category || undefined,
    description: formData.description || undefined,
    images: images.length ? images : undefined,
    endDateTime: endDateTime?.toISOString(),
    startDateTime: startDateTime?.toISOString(),
    registrationDeadline: formData.registrationDeadline?.toISOString(),
    type: formData.registrationType || undefined,
    location: formData.location || undefined,
    // Only include maxAttendeesLimit for free events
    maxAttendeesLimit:
      formData.registrationType === 'free' && formData.maxAttendees
        ? Number(formData.maxAttendees)
        : undefined,
    eventAdmins:
      'organizer' in formData && formData.organizer?.id
        ? [formData.organizer.id]
        : undefined,
    specialInstructions: formData.specialInstructions || undefined,
    tickets: eventTicketData.length ? eventTicketData : undefined,
    timeZone: formData.timeZone || undefined,
    venueAddress: formData.venueAddress || undefined,
    venueCity: formData.venueCity || undefined,
    venueName: formData.venueName || undefined,
    venueState: formData.venueState || undefined,
    zoomLink: formData.zoomLink || undefined,
    guests: guestUsers.length ? guestUsers : undefined,
    eventTags:
      Array.isArray(formData.tag) && formData.tag.length
        ? formData.tag.map(t => t.id)
        : undefined,
    venueLatitude: formData.venueLatitude
      ? Number(Number(formData.venueLatitude).toFixed(6))
      : undefined,
    venueLongitude: formData.venueLongitude
      ? Number(Number(formData.venueLongitude).toFixed(6))
      : undefined,
    venueCountry: formData.venueCountry || undefined,
    sponsorshipPackages: sponsorshipPackages,
    activityHighlights: activityHighlights,
    ...(eventPdf ? { document: eventPdf } : {}),
    ...(eventInfoPdf ? { eventInfoDocument: eventInfoPdf } : {}),
    status: EVENT_FORM_STATUS.DRAFT,
  }
}

export const validatePaidEventTickets = (
  registrationType: string,
  tickets: EventFormDataType['eventTicket']
): boolean => {
  if (registrationType !== 'paid') return true

  if (!tickets || tickets.length === 0) return false

  const allTicketsValid = tickets.every(ticket => {
    const hasName = ticket.name?.trim() !== ''
    const hasDescription = ticket.description?.trim() !== ''
    const hasPerks = ticket.perks?.trim() !== ''
    const hasPrice =
      ticket.price !== undefined &&
      ticket.price !== null &&
      ticket.price !== '' &&
      Number(ticket.price) > 0
    const hasMaxAttendees =
      ticket.maxAttendeesLimit !== undefined &&
      ticket.maxAttendeesLimit !== null &&
      ticket.maxAttendeesLimit !== ''

    return hasName && hasDescription && hasPerks && hasPrice && hasMaxAttendees
  })

  return allTicketsValid
}

export const validateDraftGuests = (
  guests: EventUser[]
): { hasPartialGuests: boolean; incompleteIndexes: number[] } => {
  const incompleteIndexes: number[] = []

  const hasPartialGuests = guests.some((guest, index) => {
    const hasName = guest.fullName?.trim() !== ''
    const hasDesignation = guest.designation?.trim() !== ''
    const hasProfileImage = !!(guest.avatarFile || guest.profile?.trim())

    const filledFieldsCount = [hasName, hasDesignation, hasProfileImage].filter(
      Boolean
    ).length

    const isPartial = filledFieldsCount > 0 && filledFieldsCount < 3
    if (isPartial) {
      incompleteIndexes.push(index)
    }
    return isPartial
  })

  return { hasPartialGuests, incompleteIndexes }
}

export const validateDraftPackages = (
  sponsorshipPackages: EventFormDataType['sponsorshipPackages']
): { hasPartialPackages: boolean; incompleteIndexes: number[] } => {
  const incompleteIndexes: number[] = []

  const hasPartialPackages = sponsorshipPackages.some((pkg, index) => {
    const hasName = pkg.name?.trim() !== ''
    const hasDescription = pkg.description?.trim() !== ''
    const hasPrice = pkg.price?.trim() !== '' && Number(pkg.price) > 0
    const hasAvailablePlacements =
      pkg.availablePlacements && pkg.availablePlacements.length > 0
    const hasPriority = pkg.priority?.trim() !== ''
    const hasMaxSponsorshipsLimit =
      pkg.maxSponsorshipsLimit?.trim() !== '' &&
      Number(pkg.maxSponsorshipsLimit) > 0

    const filledFieldsCount = [
      hasName,
      hasDescription,
      hasPrice,
      hasAvailablePlacements,
      hasPriority,
      hasMaxSponsorshipsLimit,
    ].filter(Boolean).length

    const isPartial = filledFieldsCount > 0 && filledFieldsCount < 6
    if (isPartial) {
      incompleteIndexes.push(index)
    }
    return isPartial
  })

  return { hasPartialPackages, incompleteIndexes }
}

export const validateDraftHighlights = (
  eventHighlights: EventFormDataType['eventHighlights']
): { hasPartialHighlights: boolean; incompleteIndexes: number[] } => {
  const incompleteIndexes: number[] = []

  const hasPartialHighlights = eventHighlights.some((highlight, index) => {
    const hasTitle = highlight.title?.trim() !== ''
    const hasDescription = highlight.shortDescription?.trim() !== ''

    const filledFieldsCount = [hasTitle, hasDescription].filter(Boolean).length

    const isPartial = filledFieldsCount === 1 // Either title or description is filled but not both
    if (isPartial) {
      incompleteIndexes.push(index)
    }
    return isPartial
  })

  return { hasPartialHighlights, incompleteIndexes }
}

export const setPaidEventTicketErrors = <
  T extends EventFormDataType | EventAdminEventFormDataType,
>(
  form: UseFormReturn<T>
) => {
  const tickets = form.getValues('eventTicket' as Path<T>) as
    | EventTicket[]
    | undefined

  if (!tickets || tickets.length === 0) {
    const fields: (keyof EventTicket)[] = [
      'name',
      'description',
      'perks',
      'price',
    ]
    fields.forEach(field => {
      const path = `eventTicket.0.${String(field)}` as Path<T>
      form.setError(path, {
        type: 'manual',
        message: `Ticket ${field[0].toUpperCase() + field.slice(1)} is required`,
      })
    })
    return
  }

  tickets.forEach((ticket, ticketIndex) => {
    const noName = !ticket.name || ticket.name.trim() === ''
    const noDescription =
      !ticket.description || ticket.description.trim() === ''
    const noPerks = !ticket.perks || ticket.perks.trim() === ''
    const noPrice =
      ticket.price === undefined ||
      ticket.price === null ||
      Number(ticket.price) <= 0

    const isTicketEmpty = noName && noDescription && noPerks && noPrice

    if (isTicketEmpty) {
      const fields: (keyof EventTicket)[] = [
        'name',
        'description',
        'perks',
        'price',
      ]
      fields.forEach(field => {
        const path = `eventTicket.${ticketIndex}.${String(field)}` as Path<T>
        const message = `Ticket ${field[0].toUpperCase() + field.slice(1)} is required`
        form.setError(path, { type: 'manual', message })
      })
    }
  })

  const incompleteTicketIndex = tickets.findIndex(ticket => {
    const hasName = ticket.name?.trim() !== ''
    const hasDescription = ticket.description?.trim() !== ''
    const hasPerks = ticket.perks?.trim() !== ''
    const hasPrice =
      ticket.price !== undefined &&
      ticket.price !== null &&
      Number(ticket.price) > 0

    const hasAnyField = hasName || hasDescription || hasPerks || hasPrice
    const hasAllFields = hasName && hasDescription && hasPerks && hasPrice

    return hasAnyField && !hasAllFields
  })

  if (incompleteTicketIndex !== -1) {
    const ticket = tickets[incompleteTicketIndex]
    const fields: (keyof EventTicket)[] = [
      'name',
      'description',
      'perks',
      'price',
      'maxAttendeesLimit',
    ]

    fields.forEach(field => {
      const fieldValidMap: Partial<Record<keyof EventTicket, boolean>> = {
        name: ticket.name?.trim() !== '',
        description: ticket.description?.trim() !== '',
        perks: ticket.perks?.trim() !== '',
        price:
          ticket.price !== undefined &&
          ticket.price !== null &&
          Number(ticket.price) > 0,
      }

      const fieldMessageMap: Partial<Record<keyof EventTicket, string>> = {
        name: 'Ticket name is required for paid events',
        description: 'Description is required for paid events',
        perks: "What's included is required for paid events",
        price: 'Price is required for paid events',
        maxAttendeesLimit: 'Max attendees limit is required for paid events',
      }

      if (!fieldValidMap[field]) {
        const path =
          `eventTicket.${incompleteTicketIndex}.${String(field)}` as Path<T>
        form.setError(path, {
          type: 'manual',
          message: fieldMessageMap[field]!,
        })
      }
    })
  }
}

export const setGuestErrors = <
  T extends EventFormDataType | EventAdminEventFormDataType,
>(
  form: UseFormReturn<T>,
  incompleteIndexes: number[]
) => {
  const guests = form.getValues('users' as Path<T>) as EventUser[] | undefined
  if (!guests) return

  incompleteIndexes.forEach(index => {
    const guest = guests[index]
    if (!guest) return

    const hasName = guest.fullName?.trim() !== ''
    const hasDesignation = guest.designation?.trim() !== ''
    const hasProfileImage = !!(guest.avatarFile || guest.profile?.trim())

    const setNestedError = (field: keyof EventUser, message: string) => {
      const path = `users.${index}.${String(field)}` as Path<T>
      form.setError(path, { type: 'manual', message })
    }

    if (!hasName)
      setNestedError('fullName', 'Guest name is required when adding a guest')
    if (!hasDesignation)
      setNestedError(
        'designation',
        'Designation is required when adding a guest'
      )
    if (!hasProfileImage)
      setNestedError(
        'avatarFile',
        'Profile image is required when adding a guest'
      )
  })
}

export const setPackageErrors = <
  T extends EventFormDataType | EventAdminEventFormDataType,
>(
  form: UseFormReturn<T>,
  incompleteIndexes: number[]
) => {
  const sponsorshipPackages = form.getValues(
    'sponsorshipPackages' as Path<T>
  ) as EventFormDataType['sponsorshipPackages'] | undefined

  if (!sponsorshipPackages) return

  incompleteIndexes.forEach(index => {
    const pkg = sponsorshipPackages[index]
    if (!pkg) return

    const hasName = pkg.name?.trim() !== ''
    const hasDescription = pkg.description?.trim() !== ''
    const hasPrice = pkg.price?.trim() !== '' && Number(pkg.price) > 0
    const hasAvailablePlacements =
      pkg.availablePlacements && pkg.availablePlacements.length > 0
    const hasPriority = pkg.priority?.trim() !== ''
    const hasMaxSponsorshipsLimit =
      pkg.maxSponsorshipsLimit?.trim() !== '' &&
      Number(pkg.maxSponsorshipsLimit) > 0

    const setNestedError = (field: string, message: string) => {
      const path = `sponsorshipPackages.${index}.${field}` as Path<T>
      form.setError(path, { type: 'manual', message })
    }

    if (!hasName)
      setNestedError(
        'name',
        'Package name is required when adding a sponsorship package'
      )
    if (!hasDescription)
      setNestedError(
        'description',
        'Package description is required when adding a sponsorship package'
      )
    if (!hasPrice)
      setNestedError(
        'price',
        'Package price is required when adding a sponsorship package'
      )
    if (!hasAvailablePlacements)
      setNestedError(
        'availablePlacements',
        'At least one ad slot is required when adding a sponsorship package'
      )
    if (!hasPriority)
      setNestedError(
        'priority',
        'Package priority is required when adding a sponsorship package'
      )
    if (!hasMaxSponsorshipsLimit)
      setNestedError(
        'maxSponsorshipsLimit',
        'Max sponsorships limit is required when adding a sponsorship package'
      )
  })
}

export const setHighlightErrors = <
  T extends EventFormDataType | EventAdminEventFormDataType,
>(
  form: UseFormReturn<T>,
  incompleteIndexes: number[]
) => {
  const eventHighlights = form.getValues('eventHighlights' as Path<T>) as
    | EventFormDataType['eventHighlights']
    | undefined

  if (!eventHighlights) return

  incompleteIndexes.forEach(index => {
    const highlight = eventHighlights[index]
    if (!highlight) return

    const hasTitle = highlight.title?.trim() !== ''
    const hasDescription = highlight.shortDescription?.trim() !== ''

    const setNestedError = (field: string, message: string) => {
      const path = `eventHighlights.${index}.${field}` as Path<T>
      form.setError(path, { type: 'manual', message })
    }

    if (!hasTitle)
      setNestedError(
        'title',
        'Event highlight title is required when adding a highlight'
      )
    if (!hasDescription)
      setNestedError(
        'shortDescription',
        'Event highlight description is required when adding a highlight'
      )
  })
}
